import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import axios from "axios";
import { useSelector } from "react-redux";
import { fetchIndividualReport, API_BASE_URL } from "../../api/endpoint";
import { capitalize } from "../../utils/format";

const NotificationDetailsModal = ({ isViewDetailsOpen, closeDetailsModal, reportId }) => {
  const { auth } = useSelector((state) => state.login);

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!reportId || !isViewDetailsOpen) return;

    const loadDetails = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${fetchIndividualReport}${reportId}`, {
          headers: { Authorization: `Bearer ${auth}` },
        });
        setDetails(res?.data);
      } catch (err) {
        console.error("Notification details error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [reportId, isViewDetailsOpen, auth]);

  const attachmentUrl = details?.attachmentUrl ? `${API_BASE_URL}public/images/${details.attachmentUrl}` : null;

  const renderGameDetails = (game) => (
    <div className="mt-3">
      <h6 className="fw-bold mb-2 text-dark">Game Information</h6>
      <table className="table table-borderless">
        <tbody>
          <tr>
            <td className="fw-semibold" style={{ width: "35%" }}>
              Game:
            </td>
            <td>{game?.gameName}</td>
          </tr>
          <tr>
            <td className="fw-semibold">Sport:</td>
            <td>{game?.sportName}</td>
          </tr>
          <tr>
            <td className="fw-semibold">Description:</td>
            <td>{game?.gameDescription}</td>
          </tr>
          <tr>
            <td className="fw-semibold">Host:</td>
            <td>{game?.hostName}</td>
          </tr>
          <tr>
            <td className="fw-semibold">Level:</td>
            <td>{game?.level}</td>
          </tr>
          <tr>
            <td className="fw-semibold">Start Time:</td>
            <td>{game?.startTime}</td>
          </tr>
        </tbody>
      </table>

      <h6 className="fw-bold mt-3 mb-2 text-dark">Participants</h6>
      <table className="table table-borderless">
        <tbody>
          {game?.participants?.map((p, idx) => (
            <tr key={idx}>
              <td>{p?.name}</td>
              <td>{p?.email}</td>
            </tr>
          )) || (
            <tr>
              <td colSpan={2} className="text-muted text-center">
                No participants
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderUserDetails = (user) => (
    <div className="mt-3">
      <h6 className="fw-bold mb-2 text-dark">User Information</h6>
      <table className="table table-borderless table-sm">
        <tbody>
          <tr>
            <td className="fw-semibold" style={{ width: "35%" }}>
              Name:
            </td>
            <td>{user?.name}</td>
          </tr>
          <tr>
            <td className="fw-semibold">Email:</td>
            <td>{user?.email}</td>
          </tr>
          <tr>
            <td className="fw-semibold">Age:</td>
            <td>{user?.age}</td>
          </tr>
          <tr>
            <td className="fw-semibold">City:</td>
            <td>{user?.city}</td>
          </tr>
          <tr>
            <td className="fw-semibold">Reliability:</td>
            <td>{user?.reliabilityNum}%</td>
          </tr>
        </tbody>
      </table>

      {user?.avatarUrl && (
        <div className="mt-2">
          <img src={user?.avatarUrl} alt="avatar" style={{ maxWidth: 120, borderRadius: 10 }} />
        </div>
      )}
    </div>
  );

  const renderChatDetails = (participants) => (
    <div className="mt-3">
      <h6 className="fw-bold mb-2 text-dark">Chat Participants</h6>
      <table className="table table-borderless">
        <tbody>
          {participants?.length > 0 ? (
            participants?.map((p, idx) => (
              <tr key={idx}>
                <td>{p?.name}</td>
                <td>{p?.email}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={2} className="text-muted text-center">
                No participants
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <Modal show={isViewDetailsOpen} onHide={closeDetailsModal} centered size="lg">
      <div className="modal-header">
        <h5 className="modal-title">Report Details</h5>
        <button className="btn-close" onClick={closeDetailsModal}></button>
      </div>

      <div className="modal-body">
        {loading && <div className="text-center">Loading...</div>}

        {!loading && details && (
          <>
            <div>
              <strong>Report type:</strong> {capitalize(details?.type)}
              <br />
              <strong>Reporter:</strong> {details?.reporterName}
              <br />
              <strong>Reason:</strong> {details?.reason}
            </div>

            {details.type === "USER" && renderUserDetails(details?.additionalData)}
            {details.type === "GAME" && renderGameDetails(details?.additionalData)}
            {details.type === "CHAT" && renderChatDetails(details?.additionalData)}

            {attachmentUrl && (
              <div className="mt-3">
                <h6 className="fw-semibold">Attachment</h6>
                <img
                  src={attachmentUrl}
                  alt="attachment"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 250,
                    borderRadius: 8,
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default NotificationDetailsModal;
